import React, { useState, useEffect } from 'react';
import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import { enqueueSnackbar } from 'notistack';

interface Props {
  open: boolean;
  subjectLabel: string;
  onClose: () => void;
}

const DESTINATIONS = [
  { key: 'erp', name: 'ERP System', sub: 'Push product + compliance data out' },
  {
    key: 'ems',
    name: 'Export Management System',
    sub: 'Filing & shipment management',
  },
  {
    key: 'lms',
    name: 'License Management System',
    sub: 'License records & burn-down',
  },
  { key: 'api', name: 'API / File Drop', sub: 'Generic webhook or SFTP' },
];

const PACKAGE_CONTENTS = [
  'Product data & identification',
  'Classification results (HTS / jurisdiction)',
  'Screening results & risk',
  'License details & coverage',
  'Linked entities',
  'Supporting documents',
];

const SendPackageDialog: React.FC<Props> = ({ open, subjectLabel, onClose }) => {
  const [dest, setDest] = useState('erp');

  useEffect(() => {
    if (open) setDest('erp');
  }, [open]);

  const send = () => {
    enqueueSnackbar(
      `Package for ${subjectLabel} sent to ${
        DESTINATIONS.find((d) => d.key === dest)?.name
      } (preview).`,
      { variant: 'success' },
    );
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} className='eh-modal-root'>
      <div className='eh-modal' role='dialog' aria-label='Send Package'>
        <div className='eh-modal-head'>
          <div>
            <h2 className='eh-modal-title'>Send Package</h2>
            <p className='eh-modal-subtitle'>
              Push a vetted compliance package downstream — {subjectLabel}.
              <span className='export-hub-preview-pill'>Preview</span>
            </p>
          </div>
          <button
            className='eh-modal-close'
            onClick={onClose}
            aria-label='Close'
            type='button'
          >
            <CloseIcon style={{ fontSize: 20 }} />
          </button>
        </div>

        <div className='eh-modal-body'>
          <div className='eh-field'>
            <span className='eh-field-label'>Destination</span>
            <div className='eh-screen-types'>
              {DESTINATIONS.map((d) => (
                <button
                  key={d.key}
                  type='button'
                  className={`eh-screen-type ${dest === d.key ? 'active' : ''}`}
                  onClick={() => setDest(d.key)}
                >
                  <span className='eh-screen-type-text'>
                    <span className='eh-screen-type-name'>{d.name}</span>
                    <span className='eh-screen-type-desc'>{d.sub}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className='eh-field'>
            <span className='eh-field-label'>Package contents</span>
            <ul className='eh-factor-list'>
              {PACKAGE_CONTENTS.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className='eh-modal-foot'>
          <button className='btn-outline' type='button' onClick={onClose}>
            Cancel
          </button>
          <button className='btn-primary' type='button' onClick={send}>
            <SendIcon style={{ fontSize: 16 }} /> Send Package
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SendPackageDialog;
